import { usePost } from 'hooks/use-post';
import { SetStateAction, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { postLockedPonds } from 'views/production-systems/context/api/create-lock-ponds';
import { postReleasePond } from 'views/production-systems/context/api/create-release-ponds';
import { postExtendLockedPonds } from 'views/production-systems/context/api/extend-locked-ponds.ts';
import { LockedPonds } from 'views/production-systems/types/locked-ponds/locked-ponds.type';

const GetInitialLockedPonds = () => ({ granted: [], notGranted: [] });

interface UsePondActionsProps {
  globalSiteId: string;
  setLockedPonds: React.Dispatch<SetStateAction<LockedPonds>>;
}

export const usePondActions = ({ globalSiteId, setLockedPonds }: UsePondActionsProps) => {
  const queryClient = useQueryClient();

  const handleSetLockedPonds = useCallback(
    (pondIds: string[]) =>
      setLockedPonds((prev) => ({
        granted: prev.granted.filter((id) => !pondIds.includes(id)),
        notGranted: [],
      })),
    [setLockedPonds]
  );

  const lockPondsMutation = usePost<LockedPonds, { pondIds: string[] }>(
    (request) => postLockedPonds(globalSiteId, { pondIds: request.pondIds }),
    (data) => {
      setLockedPonds((prevLockedPonds) => ({
        granted: prevLockedPonds.granted.concat(data.granted),
        notGranted: prevLockedPonds.notGranted.concat(data.notGranted),
      }));
    },
    (error) => {
      console.error('Lock ponds error:', error);
      setLockedPonds(GetInitialLockedPonds());
      queryClient.invalidateQueries({
        queryKey: ['getPonds']
      });
    }
  );

  const releaseLockedPondsMutation = usePost<any, { pondIds: string[] }>((request) =>
    postReleasePond(globalSiteId, { pondIds: request.pondIds })
      .then(() => handleSetLockedPonds(request.pondIds))
      .catch((err) => {
        console.error('Release ponds error:', err);
        setLockedPonds(GetInitialLockedPonds());
        queryClient.invalidateQueries({
          queryKey: ['getPonds']
        });
      })
  );

  const extendLockedPondsMutation = usePost<number, { pondIds: string[] }>((request) =>
    postExtendLockedPonds(globalSiteId, { pondIds: request.pondIds }).catch((err) => {
      console.error('Extend ponds error:', err);
      setLockedPonds(GetInitialLockedPonds());
      queryClient.invalidateQueries({
        queryKey: ['getPonds']
      });
    })
  );
  const { mutate: mutateLockPonds } = lockPondsMutation;
  const { mutate: mutateReleasePonds } = releaseLockedPondsMutation;
  const { mutate: mutateExtendPonds } = extendLockedPondsMutation;

  const handleLockPonds = useCallback(
    (pondIds: string[]) => {
      mutateLockPonds({ pondIds });
    },
    [mutateLockPonds]
  );

  const handleReleasePonds = useCallback(
    (pondIds: string[]) => {
      mutateReleasePonds({ pondIds });
    },
    [mutateReleasePonds]
  );

  const handleExtendPonds = useCallback(
    (pondIds: string[]) => {
      mutateExtendPonds({ pondIds });
    },
    [mutateExtendPonds]
  );

  return {
    lockPonds: handleLockPonds,
    releasePonds: handleReleasePonds,
    extendPonds: handleExtendPonds,
    lockError: lockPondsMutation.error,
  };
};
